/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';

import {ElementKeyboardEvent, ElementMouseEvent} from './Types';

type AnyKeyboardEvent =
  | KeyboardEvent
  | ElementKeyboardEvent
  | JQueryKeyEventObject
  | React.KeyboardEvent<HTMLElement>;

const ctrl = benchlingUtil.isMac() ? '\u2318' : 'Ctrl';

const META_KEY_TEXT = benchlingUtil.isMac() ? '\u2318' : '\u229e';
const ALT_KEY_TEXT = benchlingUtil.isMac() ? 'Option' : 'Alt';
const HOME_KEY_TEXT = benchlingUtil.isMac() ? 'Fn + Left Arrow' : 'Home';
const END_KEY_TEXT = benchlingUtil.isMac() ? 'Fn + Right Arrow' : 'End';
const PAGE_UP_KEY_TEXT = benchlingUtil.isMac() ? 'Fn + Up Arrow' : 'PageUp';
const PAGE_DOWN_KEY_TEXT = benchlingUtil.isMac() ? 'Fn + Down Arrow' : 'PageDown';

const MODIFIER_KEYS = [
  'Alt',
  'Control',
  'Meta', // cmd
  'OS', // firefox cmd
  'Shift',
];

const NON_TYPING_KEYS = [
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'Control',
  'End',
  'Enter',
  'Fn',
  'Home',
  'Meta', // cmd
  'OS', // firefox cmd
  'PageDown',
  'Pageup',
  'Tab',
  // List is non-exhaustive.
];

/* Record the exact keyboard data for logging. */
function getShortcutText(e: AnyKeyboardEvent): string {
  const text = _.compact([
    e.metaKey && META_KEY_TEXT,
    e.ctrlKey && 'Ctrl',
    e.altKey && ALT_KEY_TEXT,
    e.shiftKey && 'Shift',
    !MODIFIER_KEYS.includes(e.key) && e.key,
  ]).join(' + ');
  // .which is deprecated but logged since our keyboard code uses it often.
  return `${text} (${e.which})`;
}

/* Get case-insensitive and OS-agnostic key combination for determining which shortcut is pressed.

'Ctrl' here represents the Cmd key on a Mac and the Ctrl key otherwise.
*/
function getNormalizedShortcut(e: AnyKeyboardEvent): string {
  const text = [
    isOSCtrlKey(e) && 'Ctrl',
    e.altKey && 'Alt',
    e.shiftKey && 'Shift',
    e.key && !MODIFIER_KEYS.includes(e.key) && e.key.toUpperCase(),
  ]
    .filter((x) => x)
    .join(' + ');
  return text;
}

/** Convert a normalized shortcut to the OS-specific text representation. */
function getOsSpecificShortcutText(normalizedShortcut: string): string {
  return normalizedShortcut
    .replaceAll('Ctrl', ctrl)
    .replaceAll('Alt', ALT_KEY_TEXT)
    .replaceAll('Meta', META_KEY_TEXT)
    .replaceAll('Home', HOME_KEY_TEXT)
    .replaceAll('End', END_KEY_TEXT)
    .replaceAll('PageUp', PAGE_UP_KEY_TEXT)
    .replaceAll('PageDown', PAGE_DOWN_KEY_TEXT);
}

function isNonTypingKey(e: AnyKeyboardEvent): boolean {
  return (
    (e.target && !$(e.target).closest('input, [contentEditable]:not(.hiddenFocusEditable), textarea')) ||
    e.metaKey ||
    e.ctrlKey ||
    NON_TYPING_KEYS.includes(e.key)
  );
}

function isOSCtrlKey(
  e: KeyboardEvent | ElementKeyboardEvent | React.MouseEvent<HTMLElement | SVGGElement> | JQueryKeyEventObject
): boolean {
  if (benchlingUtil.isMac()) {
    return e.metaKey;
  } else {
    return e.ctrlKey;
  }
}

function isTKey(e: AnyKeyboardEvent): boolean {
  return e.which === 84;
}

function isCtrlC(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + C' && e.target === e.currentTarget;
}

function isCtrlShiftC(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + Shift + C' && e.target === e.currentTarget;
}

function isCtrlA(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + A' && e.target === e.currentTarget;
}

function isCtrlI(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + I' && e.target === e.currentTarget;
}

function isCtrlX(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + X' && e.target === e.currentTarget;
}

function isCtrlV(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + V' && e.target === e.currentTarget;
}

function isCtrlZ(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + Z' && e.target === e.currentTarget;
}

function isCtrlF(e: KeyboardEvent | ElementKeyboardEvent | JQueryEventObject): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + F';
}

function isCtrlShiftF(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + Shift + F';
}

function isCtrlG(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + G';
}

function isCtrlShiftG(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + Shift + G';
}

function isCtrlS(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + S';
}

function isCtrlP(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + P';
}

function isHomeKey(e: AnyKeyboardEvent): boolean {
  // Equivalent is Fn + Left Arrow on Mac
  return e.which === 36 || e.key === 'Home';
}

function isEndKey(e: AnyKeyboardEvent): boolean {
  // Equivalent is Fn + Right Arrow on Mac
  return e.which === 35 || e.key === 'End';
}

function isPageUpKey(e: AnyKeyboardEvent): boolean {
  // Equivalent is Fn + Up Arrow on Mac
  return e.which === 33 || e.key === 'PageUp';
}

function isPageDownKey(e: AnyKeyboardEvent): boolean {
  // Equivalent is Fn + Down Arrow on Mac
  return e.which === 34 || e.key === 'PageDown';
}

function isShiftEnter(e: AnyKeyboardEvent): boolean {
  return e.shiftKey && isEnterKey(e);
}

function isShiftSpace(e: AnyKeyboardEvent): boolean {
  return e.shiftKey && isSpaceKey(e);
}

function isCtrlSpace(e: AnyKeyboardEvent): boolean {
  return e.ctrlKey && isSpaceKey(e);
}

function isUndo(e: AnyKeyboardEvent): boolean {
  return getNormalizedShortcut(e) === 'Ctrl + Z';
}

function isRedo(e: AnyKeyboardEvent): boolean {
  const normalized = getNormalizedShortcut(e);
  return normalized === 'Ctrl + Shift + Z' || normalized === 'Ctrl + Y';
}

function isUndoRedo(e: AnyKeyboardEvent): boolean {
  return isUndo(e) || isRedo(e);
}

function isCtrlQuestionMark(e: AnyKeyboardEvent): boolean {
  return isOSCtrlKey(e) && e.shiftKey && (e.key === '?' || e.key === '/');
}

function isControlKey(e: AnyKeyboardEvent): boolean {
  // 91, 93 (Webkit, left/right) and 224 (Firefox) and 17 (Ctrl)
  // We don't use e.metaKey, e.ctrlKey because we only want to capture the bare keypresses.
  return (
    e.which === 17 ||
    e.which === 91 ||
    e.which === 93 ||
    e.which === 224 ||
    e.key === 'Control' ||
    e.key === 'Meta'
  );
}

function isLeftKey(e: AnyKeyboardEvent): boolean {
  return e.which === 37 || e.key === 'ArrowLeft';
}

function isUpKey(e: AnyKeyboardEvent): boolean {
  return e.which === 38 || e.key === 'ArrowUp';
}

function isRightKey(e: AnyKeyboardEvent): boolean {
  return e.which === 39 || e.key === 'ArrowRight';
}

function isDownKey(e: AnyKeyboardEvent): boolean {
  return e.which === 40 || e.key === 'ArrowDown';
}

function isAnArrowKey(e: AnyKeyboardEvent): boolean {
  return (
    [37, 38, 39, 40].includes(e.which) || ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(e.key)
  );
}

function getArrowKeyDirection(e: AnyKeyboardEvent): 'up' | 'down' | 'left' | 'right' {
  if (!isAnArrowKey(e)) {
    throw new Error('Must use with an arrow key event');
  }
  if (isUpKey(e)) {
    return 'up';
  }
  if (isDownKey(e)) {
    return 'down';
  }
  if (isLeftKey(e)) {
    return 'left';
  }
  return 'right';
}

function isEnterKey(e: AnyKeyboardEvent): boolean {
  return e.which === ENTER || e.key === 'Enter';
}

function isTabKey(e: AnyKeyboardEvent): boolean {
  return e.which === 9 || e.key === 'Tab';
}

function isSpaceKey(e: AnyKeyboardEvent): boolean {
  return e.which === 32 || e.key === ' ';
}

function isBackspaceKey(e: AnyKeyboardEvent): boolean {
  return e.which === 8 || e.key === 'Backspace';
}

function isADeleteKey(e: AnyKeyboardEvent): boolean {
  return isBackspaceKey(e) || e.which === 46 || e.key === 'Delete';
}

function isEscapeKey(e: AnyKeyboardEvent): boolean {
  return e.which === 27 || e.key === 'Escape';
}

function isSemicolonKey(e: AnyKeyboardEvent): boolean {
  return e.which === SEMICOLON || e.key === ';';
}

function isCommaKey(e: AnyKeyboardEvent): boolean {
  return e.which === COMMA || e.key === ',';
}

function onlyEscapeKey<T, R>(f: (this: T) => R, ctx: T): (e: AnyKeyboardEvent) => R | undefined {
  return function (e: AnyKeyboardEvent): R | undefined {
    if (isEscapeKey(e)) {
      return f.call(ctx);
    } else {
      return undefined;
    }
  };
}

/**
 * When used as a keydown / keyup handler, calls callback `f` when the key that was pressed is the Enter key.
 * @param f Callback to be called when Enter key is pressed
 * @example
 * ```
 * <span
 *   role="link"
 *   onClick={() => {
 *     navigateToURL(props.href);
 *   }}
 *   onKeyDown={
 *     callOnEnter(() => {
 *       navigateToURL(props.href);
 *     });
 *   }
 *   tabIndex={0}
 * >
 *   My Cool Sequence
 * </span>
 * ```
 */
function callOnEnter(
  f: ((e: ElementKeyboardEvent) => void) | undefined
): (event: ElementKeyboardEvent) => void {
  return (e: ElementKeyboardEvent) => {
    if (isEnterKey(e) && f) {
      f(e);
    }
  };
}

/**
 * When used as a keydown / keyup handler, calls callback `f` when the key that was pressed is the Enter key
 * or the spacebar.
 * @param f Callback to be kalled when Enter key or spacebar is pressed
 * @example
 * ```
 * <BenchlingIcon
 *   altText="Close"
 *   icon="close"
 *   onClick={props.onClose}
 *   onKeyDown={
 *     callOnEnterOrSpace(() => {
 *       props.onClose();
 *     });
 *   }
 *   role="button"
 *   tabIndex={0}
 * />
 * ```
 */
function callOnEnterOrSpace(
  f: ((e: ElementKeyboardEvent) => void) | undefined
): (event: ElementKeyboardEvent) => void {
  return (e: ElementKeyboardEvent) => {
    if ((isEnterKey(e) || isSpaceKey(e)) && f) {
      f(e);
    }
  };
}

function isShiftClick(e: ElementMouseEvent): boolean {
  return e.nativeEvent instanceof MouseEvent && e.nativeEvent.shiftKey;
}

function isLetterKey(e: AnyKeyboardEvent): boolean {
  return 65 <= e.which && e.which <= 90;
}

export interface ShortcutDescription {
  description: string;
  shortcuts: Array<{
    normalizedShortcutText: string;
    skipOsTranslation?: boolean;
  }>;
}
export interface KeyboardShortcut {
  matches: (e: ElementKeyboardEvent) => boolean;
  descriptionsForModal: Array<ShortcutDescription>;
}

export const BACKSPACE = 8;
export const ENTER = 13;
export const ESCAPE = 27;
export const SEMICOLON = 186;
export const EQUAL = 187;
export const COMMA = 188;
export const PERIOD = 190;
export const BACKTICK = 192;
export const BACKSLASH = 220;
export {
  AnyKeyboardEvent,
  ctrl,
  ALT_KEY_TEXT,
  getArrowKeyDirection,
  isNonTypingKey,
  isTKey,
  isCtrlC,
  isCtrlShiftC,
  isCtrlA,
  isCtrlF,
  isCtrlShiftF,
  isCtrlG,
  isCtrlShiftG,
  isCtrlS,
  isCtrlP,
  isCtrlI,
  isCtrlX,
  isCtrlV,
  isCtrlZ,
  isHomeKey,
  isEndKey,
  isPageUpKey,
  isPageDownKey,
  isShiftEnter,
  isShiftSpace,
  isCtrlSpace,
  isLeftKey,
  isUpKey,
  isRightKey,
  isDownKey,
  isAnArrowKey,
  isEnterKey,
  isSpaceKey,
  isTabKey,
  isBackspaceKey,
  isADeleteKey,
  isControlKey,
  isEscapeKey,
  isSemicolonKey,
  isCommaKey,
  isOSCtrlKey,
  onlyEscapeKey,
  callOnEnter,
  callOnEnterOrSpace,
  isShiftClick,
  isUndo,
  isRedo,
  isUndoRedo,
  isCtrlQuestionMark,
  isLetterKey,
  getNormalizedShortcut,
  getShortcutText,
  getOsSpecificShortcutText,
};
